<template>
  <v-container fluid>
    <div class="quotation--buttons">
      <!-- Botón para enviar cotización -->
      <v-tooltip
        v-if="status === 'quote' && checkPermissions.changeStatus"
        bottom
      >
        <template v-slot:activator="{ on }">
          <i-btn
            color="success"
            icon="fa-paper-plane"
            outlined
            :title="$t('send_quote')"
            @click="update('send')"
            v-on="on"
          />
        </template>
      </v-tooltip>

      <!-- Botón para aprobar cotización -->
      <v-tooltip
        v-if="
          (status === 'quote' || status === 'sended_quote') &&
          checkPermissions.changeStatus
        "
        bottom
      >
        <template v-slot:activator="{ on }">
          <i-btn
            color="success"
            icon="fa-check"
            outlined
            :title="$t('aproved_quote')"
            @click="update('approved')"
            v-on="on"
          />
        </template>
      </v-tooltip>

      <!-- Botones para descargar cotización -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <i-btn
            color="error"
            icon="fa-file-download"
            outlined
            :title="$t('download', { value: $t('quote') })"
            @click="getPdf(true)"
            v-on="on"
          />
        </template>
      </v-tooltip>

      <!-- Botones para imprimir como pdf cotización -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <i-btn
            color="error"
            icon="fa-file-pdf"
            outlined
            :title="$t('print', { value: $t('quote') })"
            @click="getPdf()"
            v-on="on"
          />
        </template>
      </v-tooltip>

      <!-- Tooltip para el botón de finalizar pedido -->
      <v-tooltip
        v-if="
          status === 'production_ready' ||
          (status === 'delivery' && !completedDelivery)
        "
        bottom
      >
        <template v-slot:activator="{ on }">
          <i-btn
            color="success"
            icon="fa-check-double"
            outlined
            :title="$t('finished')"
            @click="update('finished')"
            v-on="on"
          />
        </template>
      </v-tooltip>

      <v-spacer />
      <!-- Botón para cancelar cotización -->
      <v-tooltip v-if="status !== 'draft' && status !== 'canceled'" bottom>
        <template v-slot:activator="{ on }">
          <i-btn
            color="error"
            icon="fa-times"
            outlined
            :title="$t('cancel_specific', { value: $t('quote') })"
            @click="update('canceled')"
            v-on="on"
          />
        </template>
      </v-tooltip>

      <!-- Botón para agregar un producto -->
      <v-tooltip
        v-if="status === 'quote' && checkPermissions.addSuborder"
        bottom
      >
        <template v-slot:activator="{ on }">
          <i-btn
            color="primary"
            icon="fa-cart-plus"
            outlined
            :title="$tc('add_product')"
            @click="$emit('addProd')"
            v-on="on"
          />
        </template>
      </v-tooltip>
    </div>

    <!-- Cotización -->
    <v-row class="mt-0 mb-4">
      <!-- Nro de Cotización -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          outlined
          disabled
          name="quote"
          prepend-inner-icon="mdi-pound"
          readonly
          :label="$t('quote')"
          :value="quote.code"
        />
      </v-col>

      <!-- Fecha del pedido -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          outlined
          disabled
          name="date"
          readonly
          :label="$tc('date', 1)"
          :value="dateFormat(order.date)"
        />
      </v-col>

      <!-- Fecha compromiso -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          outlined
          disabled
          name="promise_date"
          readonly
          :label="$tc('promise_date', 1)"
          :value="dateFormat(order.promise_date)"
        />
      </v-col>

      <!-- Nombre del pedido -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          outlined
          disabled
          name="order_name"
          readonly
          :label="$tc('order_name', 1)"
          :value="`${order.nameOrder}`"
        />
      </v-col>
    </v-row>

    <!-- Cliente -->
    <v-row class="my-0">
      <!-- Nombre de la empresa del cliente -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          outlined
          disabled
          name="customer"
          readonly
          :label="$tc('customerCompany', 1)"
          :value="customer.company_name"
        />
      </v-col>

      <!-- Nombre del cliente -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          disabled
          name="firstname"
          outlined
          readonly
          :label="$t('name')"
          :value="customer.first_name"
        />
      </v-col>

      <!-- Apellidos del cliente -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          disabled
          name="lastname"
          outlined
          readonly
          :label="$t('last_name')"
          :value="customer.last_name"
        />
      </v-col>

      <!-- Email del cliente -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          outlined
          disabled
          name="email"
          readonly
          :label="$tc('email', 1)"
          :value="`${customer.email}`"
        />
      </v-col>

      <!-- Teléfono del cliente -->
      <v-col cols="12" sm="6" md="4" lg="3">
        <v-text-field
          outlined
          disabled
          name="phone"
          readonly
          :label="$tc('phone', 1)"
          :value="`${customer.phone}`"
        />
      </v-col>

      <!-- Campos para las direcciones del cliente -->
      <v-col
        v-for="(address, inx) in customer.address_set"
        :key="`address-${inx}`"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-text-field
          outlined
          disabled
          readonly
          :label="$tc(getAddressByType(address.address_type), 1)"
          :name="getAddressByType(address.address_type)"
          :value="getAddressName(address)"
        />
      </v-col>
    </v-row>

    <!-- Lista de productos -->
    <v-list v-if="cartlist.length > 0" class="quotation--suborders-container">
      <v-list-group
        v-for="(suborder, key) in cartlist"
        :key="`prouct-${key}`"
        class="quotation--suborders"
        color="auxtext"
        @click="searchProductInfo(suborder.pk)"
      >
        <v-icon slot="appendIcon" size="14">fas fa-arrow-down</v-icon>
        <template v-slot:activator>
          <div class="w-100">
            <!-- Datos primarios de la suborden -->
            <div class="quotation--suborders-head">
              <div>
                <!-- Nombre de la suborden -->
                <p v-if="suborder.name" class="head-title">
                  {{ suborder.name }}
                </p>

                <!-- Código de la suborden -->
                <p v-if="suborder.code" class="head-title">
                  {{ `${$tc('code', 2)}: ` }}
                  <span class="text-aux">
                    {{ suborder.code }}
                  </span>
                </p>
              </div>

              <!-- Botones para editar y eliminar suborden -->
              <div
                class="d-flex justify-content-between align-items-center g-5"
              >
                <div v-if="canBe.modified" class="d-flex g-1">
                  <i-btn
                    v-if="checkPermissions.editSuborder"
                    :title="$tc('edit', 1)"
                    outlined
                    icon="mdi-pencil"
                    color="info"
                    @click.stop="$refs.dialprod.open(suborder.pk)"
                  />
                  <i-btn
                    v-if="checkPermissions.deleteSuborder"
                    :title="$tc('delete', 1)"
                    outlined
                    icon="fa-trash"
                    color="error"
                    @click.stop="remove(suborder.pk)"
                  />
                </div>

                <!-- Switch para la entrega del producto -->
                <v-switch
                  class="v-switch mt-0"
                  color="primary"
                  key="delivery-input"
                  name="delivery"
                  hide-details
                  :disabled="!canBe.modified"
                  @click.stop="editDelivery(suborder.pk, suborder.delivery)"
                  v-model="suborder.delivery"
                >
                  <template v-slot:label>
                    <small>{{ $t('at_home') }}</small>
                  </template>
                </v-switch>
              </div>
            </div>

            <!-- Detalles de la orden -->
            <v-row class="quotation--suborders-body">
              <!-- Nombre del producto -->
              <v-col cols="12" md="3">
                <p class="body-title">{{ $tc('product', 1) }}:</p>
                <p class="body-text">{{ suborder.own_prod }}</p>
              </v-col>

              <!-- Cantidad del producto -->
              <v-col class="flex-grow-1" cols="12" md="auto">
                <p class="body-title">{{ `${$tc('amount', 2)}: ` }}</p>
                <p class="body-text">
                  {{ suborder.quantity }}
                </p>
              </v-col>

              <!-- Precio del producto -->
              <v-col class="flex-grow-1" cols="12" md="auto">
                <p class="body-title">{{ `${$tc('price', 1)}: ` }}</p>
                <p class="body-text">
                  <i-money :value="suborder.price" mode="text" />
                </p>
              </v-col>

              <!-- Subtotal del producto -->
              <v-col class="flex-grow-1" cols="12" md="auto">
                <p class="body-title">{{ `${$tc('subtotal', 1)}: ` }}</p>
                <p class="body-text">
                  <i-money
                    :value="
                      Number(suborder.subtotal) + Number(suborder.discount)
                    "
                    mode="text"
                  />
                </p>
              </v-col>

              <!-- Descuento del producto -->
              <v-col v-if="suborder.discount > 0" cols="12" md="auto">
                <p class="body-title red--text">
                  {{ `${$tc('discount', 1)}: ` }}
                </p>
                <p class="body-text red--text">
                  <i-money :value="suborder.discount" mode="text" />
                </p>
              </v-col>

              <!-- Impuesto del producto -->
              <v-col class="flex-grow-1" cols="12" md="auto">
                <p class="body-title">
                  {{ `${$tc('tax', 1)}: ` }}
                </p>
                <p class="body-text">
                  <i-money :value="suborder.tax" mode="text" />
                </p>
              </v-col>

              <!-- Total del producto -->
              <v-col class="flex-grow-1" cols="12" md="auto">
                <p class="body-title">
                  {{ `${$tc('total', 1)}: ` }}
                </p>
                <p class="body-text">
                  <i-money :value="suborder.total" mode="text" />
                </p>
              </v-col>
            </v-row>
          </div>
        </template>

        <v-row class="m-0">
          <!-- Dimensiones -->
          <v-col v-if="checkdimensions(suborder.dimensions)" cols="12">
            <h5 class="primary--text font-weight-black">
              {{ $t('dimension') }}
            </h5>
            <ul>
              <li
                v-for="(attr, key) in suborder.dimensions"
                :key="`${'dimension'}-${key}`"
              >
                {{ attr.name }}:
                <span v-if="attr.measure !== 'in' && attr.measure !== 'ft'">
                  {{ attr.value }} {{ attr.measure }}
                </span>
                <span v-if="attr.measure === 'in'">
                  {{ attr.main }}
                  {{ attr.decimalText }}
                  {{ attr.measure }}
                </span>
                <span v-if="attr.measure === 'ft'">
                  {{ attr.main }} ft {{ attr.second }} {{ attr.decimalText }} in
                </span>
                <div v-if="attr.rounded && attr.value" class="caption">
                  <span>{{ $t('rounded2') }}:</span>
                  <span class="font-weight-bold">
                    {{ attr.rounded }}
                    {{
                      attr['defaultMeasure'] !== undefined
                        ? attr['defaultMeasure']
                        : attr['measure']
                    }}
                  </span>
                </div>
              </li>
            </ul>
          </v-col>

          <!-- Descripción de la suborden -->
          <v-col v-if="suborder.description" cols="12">
            <h5 class="primary--text font-weight-black mb-0">
              {{ $t('suborder_description') }}
            </h5>
            <p class="mb-0" v-html="suborder.description"></p>
          </v-col>

          <!-- Información adicional de la suborden -->
          <v-col v-if="suborder.additional_side" cols="12">
            <h5 class="primary--text font-weight-black mb-0">
              {{ $t('additional_side') }}
            </h5>
            <p class="mb-0" v-html="suborder.additional_side"></p>
          </v-col>
        </v-row>

        <v-row v-if="informationExtra[suborder.pk]" class="m-0 mb-3">
          <!-- Fechas extras -->
          <v-col
            v-if="
              informationExtra[suborder.pk]?.dates &&
              informationExtra[suborder.pk].dates.length > 0
            "
            cols="12"
          >
            <p class="title-responsive primary--text">
              {{ $t('dates') }}
            </p>
            <ul>
              <li
                v-for="(date, keydate) in informationExtra[
                  suborder.pk
                ].dates.filter((m) => !m.changeDate)"
                :key="'dates-' + keydate"
              >
                {{ `${date.typeName}: ${dateFormat(date.date)}` }}
              </li>
            </ul>
          </v-col>

          <!-- Detalles de producción -->
          <v-col
            v-if="
              informationExtra[suborder.pk]?.details &&
              informationExtra[suborder.pk].details.length
            "
            cols="12"
          >
            <p class="title-responsive primary--text">
              {{ $t('details_production') }}
            </p>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>{{ $t('detail') }}</th>
                    <th>{{ $tc('price', 2) }}</th>
                    <th>{{ $tc('price', 0) }}</th>
                    <th>{{ $tc('amount', 2) }}</th>
                    <th>{{ $t('subtotal') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(detail, index) in informationExtra[suborder.pk]
                      .details"
                    :key="`detail-${index}`"
                  >
                    <td>{{ detail.name }}</td>
                    <td>{{ $t(detail.price_type) }}</td>
                    <td>
                      <i-money :value="detail.price" mode="text" />
                    </td>
                    <td>{{ suborder.quantity }}</td>
                    <td>
                      <i-money
                        mode="text"
                        :value="calculateSubtotal(detail, suborder.quantity)"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-list-group>
    </v-list>

    <!-- Mensaje si no hay productos en el carrito -->
    <div v-else class="quotation--without-suborders">
      <p class="quotation--text grey--text">
        {{ $t('select_prod') }}
      </p>
      <p
        v-if="status === 'quote' && checkPermissions.addSuborder"
        class="text-center"
      >
        <v-btn color="primary" outlined @click="$emit('addProd')">
          <span class="mr-2">{{ $tc('add_product') }}</span>
          <v-icon>fa-cart-plus</v-icon>
        </v-btn>
      </p>
    </div>

    <!-- Totales -->
    <div class="text-right pt-3">
      <!-- Descuento -->
      <p v-if="discount > 0" class="mb-0">
        <i-money
          mode="text"
          class="body-1 font-weight-bold red--text"
          :label="`${$t('discount')}:`"
          :value="discount"
        />
      </p>

      <!-- Subtotal -->
      <p class="mb-0">
        <i-money
          class="body-1 font-weight-bold primary--text"
          mode="text"
          :label="`${$t('subtotal')}:`"
          :value="Number(order.subtotal) + Number(discount)"
        />
      </p>

      <!-- Impuestos -->
      <p class="mb-0">
        <i-money
          class="body-1 font-weight-bold primary--text"
          mode="text"
          :label="`${$t('tax')}:`"
          :value="order.tax"
        />
      </p>

      <!-- Total -->
      <p class="mb-0">
        <i-money
          class="headline font-weight-bold primary--text"
          mode="text"
          :label="`${$t('total')}:`"
          :value="order.total"
        />
      </p>
    </div>

    <!-- Componente para manejar productos -->
    <dial-prod
      ref="dialprod"
      :orderName="order.nameOrder"
      @prod="updateValue"
      @deleted="updateValue"
    />

    <!-- Componente para el cuerpo del email -->
    <get-body-email
      v-if="quote.state === 'sended_quote'"
      app_label="sales"
      model="quotation"
      stateMain="sended_quote"
      :condition="quote.state === 'sended_quote'"
      :customerNotification="customer.notification"
      :orderId="quote.order"
      :recipient_list="customer.email"
      :subject="$t('quote')"
      ref="getbodyemail"
    />
  </v-container>
</template>

<script>
import DialProd from './DialogProduct.vue'
import GetBodyEmail from '../email/getBodyEmail.vue'
import { mapActions, mapGetters } from 'vuex'
export default {
  components: {
    DialProd,
    GetBodyEmail
  },
  props: {
    order: {
      required: true,
      type: Object
    },
    customer: {
      required: false,
      type: Object
    },
    status: {
      type: String
    },
    cartlist: {
      type: Array,
      default: () => []
    },
    completedDelivery: {
      type: Boolean
    }
  },
  data() {
    return {
      selectedItem: null,
      informationExtra: {},
      load: false,
      mode: null,
      pkRemove: null,
      quote: {
        pk: '',
        code: '',
        state: '',
        date: '',
        mail_date: null,
        customer: null,
        salesman: null,
        order: null,
        reason: null
      }
    }
  },
  computed: {
    ...mapGetters({
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin',
      me: 'session/me'
    }),
    /**
     * Determina si el usuario tiene los permisos necesarios según la indicación
     *
     * @return {boolean}
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    checkPermissions() {
      return {
        addSuborder:
          this.getPermissions([`sales.add_orderproduct`]) &&
          this.getPermissions([`sales.change_quotation`]),
        editSuborder:
          this.getPermissions([`sales.change_orderproduct`]) &&
          this.getPermissions([`sales.change_quotation`]),
        deleteSuborder:
          this.getPermissions([`sales.delete_orderproduct`]) &&
          this.getPermissions([`sales.change_quotation`]),
        changeStatus:
          this.getPermissions([`sales.change_orderstatus`]) &&
          this.getPermissions([`sales.change_quotation`])
      }
    },
    /**
     * Calcula los descuentos aplicados en los productos del carrito.
     * Devuelve la cantidad de descuentos formateado a dos decimales.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    discount() {
      if (this.cartlist && this.cartlist.length > 0) {
        return this.cartlist.reduce(
          (acc, current) => acc + Number(current.discount),
          0
        )
      }
      return 0
    },
    /**
     * Función para las condicionales del formulario
     *
     * @return {boolean}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    canBe() {
      return {
        modified:
          this.status === 'quote' &&
          this.quote.state !== 'approved' &&
          this.quote.state !== 'correction'
      }
    }
  },
  methods: {
    ...mapActions({
      showConfirmation: 'confirmation/confirmationValue'
    }),
    /**
     * Maneja la eliminación de un producto del carrito.
     * Muestra una confirmación antes de proceder con la eliminación.
     *
     * @param {Number} pk - Identificador del producto a eliminar.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    remove(pk) {
      this.pkRemove = pk
      this.showConfirmation({
        show: true,
        model: this.$tc('product', 1),
        title: this.$tc('delete', 1),
        type: 'error white--text',
        content: this.$t('confirm_delete'),
        alert: `${this.$t('success', {
          model: this.$tc('product', 1),
          action: this.$tc('delete', 2)
        })}`,
        confirm: async () => {
          await this.$api.sale.suborder.remove({ pk })
          this.$emit(
            'update:cartlist',
            this.cartlist.filter((m) => m.pk !== pk)
          )
          try {
            const current =
              parseInt(localStorage.getItem('componentKey') || '0', 10) || 0
            localStorage.setItem('componentKey', String(current + 1))
            window.dispatchEvent(new Event('componentKeyUpdated'))
          } catch (e) {}
        }
      })
    },
    /**
     * Descarga un archivo PDF de la cotización.
     *
     * @param {Boolean} down - Indica si se debe descargar o abrir en una nueva
     *                         ventana.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    getPdf(down) {
      const quoteCode = (this.quote && this.quote.code) || ''
      const orderCode = (this.order && this.order.code) || ''
      const qState = (this.quote && this.quote.state) || ''
      const oStatus = (this.order && this.order.mainStatus) || ''
      const isApproved =
        (qState &&
          qState !== 'quote' &&
          qState !== 'sended_quote' &&
          qState !== 'correction' &&
          qState !== 'draft') ||
        (oStatus &&
          oStatus !== 'quote' &&
          oStatus !== 'sended_quote' &&
          oStatus !== 'correction' &&
          oStatus !== 'draft')

      let code = ''
      if (isApproved && quoteCode && orderCode && quoteCode !== orderCode) {
        code = `${quoteCode}_${orderCode}`
      } else {
        code = quoteCode || orderCode
      }
      const codeSuffix = code ? `_${code}` : ''
      this.downloadFile({
        api: 'quotation',
        method: 'pdf',
        filename: `Quotation${codeSuffix}`,
        action: down ? 'download' : 'window',
        ext: 'pdf',
        opt: { pk: this.quote.order }
      })
    },
    /**
     * Genera una cadena con la dirección formateada.
     *
     * @param {Object} address - Objeto que contiene la dirección.
     * @returns {String} - Dirección formateada.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    getAddressName(address) {
      let name = `${address.address1}, `
      name += address.address2 && `${address.address2}, `
      name += `${address.city}, ${address.state}`
      return name
    },
    /**
     * Verifica si un objeto tiene propiedades.
     *
     * @param {Object} item - Objeto a verificar.
     * @returns {Boolean} - True si tiene propiedades, false en caso contrario.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    checkdimensions(item) {
      return Object.keys(item).length > 0
    },
    /**
     * Busca información adicional sobre un producto dado su ID.
     * Realiza solicitudes a la API para obtener detalles y fechas.
     *
     * @param {Number} id - Identificador del producto.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async searchProductInfo(id) {
      if (this.informationExtra[id] === undefined) {
        try {
          this.load = true
          await this.$api.suborder.date
            .show({
              pk: id,
              opt: {
                params: {
                  order: true
                }
              }
            })
            .then((response) => {
              this.$set(this.informationExtra, id, {
                ...this.informationExtra[id],
                dates: response.data
              })
            })
          await this.$api.suborder.production
            .show({
              pk: id,
              opt: {
                params: {
                  subOrder: true
                }
              }
            })
            .then((response) => {
              this.$set(this.informationExtra, id, {
                ...this.informationExtra[id],
                details: response.data
              })
            })
        } finally {
          this.load = false
        }
      }
    },
    /**
     * Actualiza el estado del pedido según el modo especificado.
     * Muestra una confirmación antes de proceder con la actualización.
     *
     * @param {String} mode - Modo de actualización
     *                        (send, canceled, approved, correction, finished,
     *                        production_ready).
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    update(mode) {
      this.mode = mode
      let state = ''
      let title = ''
      switch (mode) {
        case 'send':
          state = 'sended_quote'
          title = this.$tc('send', 0)
          break
        case 'canceled':
          state = 'canceled'
          title = this.$t('cancel')
          break
        case 'approved':
          state = 'approved'
          title = this.$t('approve')
          this.$emit('approved')
          break
        case 'correction':
          state = 'correction'
          title = this.$t('correction')
          break
        case 'finished':
          state = 'finished'
          title = ''
          break
      }
      if (state === 'canceled') {
        this.$swal({
          title: `${this.$t('cancel')} ${this.$t('quote')}`,
          text: this.$t('cancel_quotation'),
          icon: 'question',
          background: 'var(--v-auxbg-base)',
          color: 'var(--v-auxtext-base)',
          showCancelButton: true,
          cancelButtonText: this.$t('cancel'),
          confirmButtonColor: 'var(--v-primary-base)',
          confirmButtonText: this.$tc('send', 0),
          input: 'textarea',
          inputPlaceholder: this.$t('reason'),
          inputValidator: (value) => {
            if (!value) return this.$t('cancel_quotation_validation')
          }
        }).then((result) => {
          if (result.isConfirmed) {
            this.quote.reason = result.value
            this.edit(state)
          }
        })
      } else {
        this.showConfirmation({
          show: true,
          model:
            state === 'finished'
              ? this.$t('finishedAndRemove')
              : this.$tc('quote', 1),
          title: title,
          type: 'primary',
          content:
            state === 'finished'
              ? this.$t('are_sure')
              : this.$t('are_you_sure', {
                  action: title,
                  model: state === 'finished' ? '' : this.$tc('quote', 1)
                }),
          alert: `${this.$t('success', {
            model: state === 'finished' ? '' : this.$tc('quote', 1),
            action: this.$tc('update', 2)
          })}`,
          confirm: () => this.edit(state)
        })
      }
      // Señal para dar la orden de actualizar conteo de ordenes.
      localStorage.setItem('updatedKey', true)

      // Eliminación de la señal.
      setTimeout(() => {
        if (localStorage.getItem('updatedKey')) {
          localStorage.removeItem('updatedKey')
        }
      }, 60000)
    },
    /**
     * Maneja la edición del estado de la cotización.
     * Emite un evento al finalizar la operación.
     *
     * @param {String} state - Nuevo estado de la cotización.
     *
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async edit(state) {
      this.quote.state = state
      const response = await this.$api.quotation.edit({
        pk: this.quote.pk,
        form: this.quote
      })
      if (response.data && response.data.emailTemplate) {
        this.$store.dispatch(
          'utils/openDialogEmailNotification',
          response.data.emailTemplate
        )
      }
      if (state === 'approved' && Array.isArray(this.cartlist)) {
        try {
          await Promise.all(
            this.cartlist.map(async (item, idx) => {
              try {
                const response = await this.$api.sale.suborder.show({
                  pk: item.pk
                })
                // Reemplaza la entrada con los datos actualizados
                this.$set(this.cartlist, idx, response.data)
              } catch (err) {
                // Error al refrescar suborden
              }
            })
          )
        } catch (e) {
          // Error al refrescar cartlist
        }
      }

      /**
       * Emite señal para que otros componentes
       * actualicen su información. Reutilizamos `componentKey` que ya
       * usan otros componentes para forzar un refetch.
       */
      try {
        const current =
          parseInt(localStorage.getItem('componentKey') || '0', 10) || 0
        const next = current + 1
        localStorage.setItem('componentKey', String(next))
        // Dispatch global event para listeners en componentes abiertos
        try {
          window.dispatchEvent(new Event('componentKeyUpdated'))
        } catch (evErr) {
          // Error al dispatch event
        }
      } catch (lsErr) {
        // Error al acceder a localStorage
      }

      this.$emit('done')
      switch (this.quote.state) {
        case 'finished':
          this.removeRepository()
          break
        case 'sended_quote':
          // El correo se gestiona por backend o no aplica llamar modal en este punto
          break
        case 'approved':
          // Notificación PUSH delegada ahora al backend
          break
        default:
          break
      }
    },
    /**
     * Elimina el repositorio de imágenes asociado al pedido.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async removeRepository() {
      try {
        this.load = true
        await this.$api.sale.removerepository({
          pk: this.order.pk
        })
      } finally {
        this.load = false
      }
    },
    /**
     * Crea una nueva cotización o actualiza una existente.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async getQuote() {
      if (this.quote.order && this.quote.customer) {
        const d = new Date()
        this.quote.date = `${d.getFullYear()}-${(
          '0' +
          (d.getMonth() + 1)
        ).slice(-2)}-${('0' + d.getDate()).slice(-2)}`
        try {
          this.quote.state = 'quote'
          const { data: quotation } = await this.$api.quotation.create({
            form: this.quote
          })
          this.quote = quotation
          this.linkToConversation(quotation)
        } catch (error) {
          console.error('Error al crear la cotización:', error)
        }
      }
    },
    /**
     * Vincula la cotización recién creada con la conversación de WhatsApp
     * desde la que se vino a cotizar, si es que se vino de ahí.
     *
     * Falla en silencio: el vínculo es contexto comercial, no puede costar la
     * cotización que el usuario acaba de armar.
     *
     * @param {Object} quotation - Cotización creada.
     */
    async linkToConversation(quotation) {
      const conversation = this.$store.getters['whatsapp/getConversationToSale']

      if (!conversation || !quotation || !quotation.pk) {
        return
      }

      const company = this.$store.getters['company/getCompanyData']

      try {
        await this.$api.whatsapp.link({
          pk: conversation,
          form: { kind: 'quotation', id: quotation.pk },
          opt: {
            params: { company_id: company ? company.pk : undefined },
            skipErrorToast: true
          }
        })
      } catch (e) {
        // El vínculo es accesorio
      } finally {
        this.$store.commit('whatsapp/setConversationToSale', null)
      }
    },
    /**
     * Actualiza el valor de un producto en el carrito.
     * Emite un evento con la lista actualizada.
     *
     * @param {Object} evt - Evento que contiene la información del producto.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    updateValue(evt) {
      this.$emit(
        'update:cartlist',
        this.cartlist.map((m) => {
          if (m.pk === evt.pk) {
            return { ...evt, updateProduct: true }
          }
          return m
        })
      )
    },
    /**
     * Edita la información de entrega de un producto en el carrito.
     *
     * @param {Number} pk - Identificador del producto.
     * @param {Object} delivery - Información de entrega a actualizar.
     *
     * @author Miguel E. Villamizar R. <mevr02 at gmail.com>
     * @author Rosana Mendez <rosanamendez5 at gmail.com>
     */
    async editDelivery(pk, delivery) {
      let orderproduct = this.cartlist.find((m) => m.pk === pk)
      orderproduct.delivery = delivery
      let form_data = new FormData()
      for (var key in orderproduct) {
        if (orderproduct[key] == null || key === 'thumbnail') {
          continue
        }
        form_data.append(
          key,
          key !== 'dimensions'
            ? orderproduct[key]
            : JSON.stringify(orderproduct[key])
        )
      }
      await this.$api.sale.suborder.edit({
        pk: pk,
        form: form_data
      })
    },
    /**
     * Método que determina si la dirección es de facturación o envío
     *
     * @param {String} address - Tipo de dirección del cliente.
     * @returns {String} Cadena que determinará el label y la traducción.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    getAddressByType(address) {
      const data = {
        SHIP: 'shippingAddress',
        BILL: 'customerAddress'
      }
      return data[address] || 'address'
    },
    /**
     * Método que calcula el precio del subtotal de cada detalle de producción
     *
     * @param {Object} detail - Detalle de producción
     * @param {Number} quantity - Cantidad del producto
     * @returns {Number} Cálculo del subtotal
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    calculateSubtotal(detail = {}, quantity = 0) {
      if (detail.price_type === 'bool' || detail.is_multipliable) {
        return detail.price * quantity
      }
      if (detail.price) {
        return detail.price
      }
      return 0
    }
  },
  watch: {
    order: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val !== undefined && val !== null) {
          if (this.quote.order !== val.pk) {
            this.quote.order = val.pk
          }
          if (this.quote.pk === '' || this.quote.pk === null) {
            this.getQuote()
          }
        }
      }
    },
    customer: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val !== undefined && val !== null) {
          if (this.quote.customer !== val.pk) {
            this.quote.customer = val.pk
          }
          if (this.quote.pk === '' || this.quote.pk === null) {
            this.getQuote()
          }
        }
      }
    },
    cartlist: {
      deep: true,
      handler(val) {
        for (let x in val) {
          if (val[x].updateProduct) {
            delete this.informationExtra[val[x].pk]
          }
          this.searchProductInfo(val[x].pk)
        }
        const subtotal = this.cartlist
          .reduce(
            (accumulator, currentValue) =>
              accumulator + Number(currentValue.subtotal),
            0
          )
          .toFixed(2)
        const tax = this.cartlist
          .reduce(
            (accumulator, currentValue) =>
              accumulator + Number(currentValue.tax),
            0
          )
          .toFixed(2)
        const total = (Number(subtotal) + Number(tax)).toFixed(2)
        this.order.subtotal = subtotal
        this.order.tax = tax
        this.order.total = total
      }
    }
  },
  created() {
    this.unwatch = this.$store.watch(
      () => this.$store.getters['confirmation/getConfirmationFinished'],
      async (val) => {
        if (val === this.$tc('product', 1) && this.pkRemove !== null) {
          this.$store.dispatch('confirmation/confirmationFinished', false)
          this.$emit(
            'update:cartlist',
            this.cartlist.filter((m) => m.pk !== this.pkRemove)
          )
          this.pkRemove = null
        }
        if (
          (val === this.$tc('quote', 1) ||
            val === this.$t('finishedAndRemove')) &&
          this.mode !== null
        ) {
          switch (this.mode) {
            case 'send':
              this.quote.state = 'sended_quote'
              this.$emit('update:status', 'sended_quote')
              break
            case 'canceled':
              this.quote.state = 'canceled'
              this.$emit('update:status', 'canceled')
              break
            case 'approved':
              this.quote.state = 'approved'
              this.$emit('update:status', 'in_process')
              break
            case 'correction':
              this.quote.state = 'correction'
              this.$emit('update:status', 'quote')
              break
            case 'finished':
              this.quote.state = 'finished'
              this.$emit('update:status', 'finished')
              break
          }
          this.mode = null
        }
      }
    )
  },
  beforeDestroy() {
    this.unwatch()
  }
}
</script>
<style lang="sass">
.quotation--buttons
  // --------------------------------------------------
  // Contenedor de los botones superiores la cotización
  // --------------------------------------------------
  display: flex
  gap: 5px
  margin-bottom: 1rem

.v-list.quotation--suborders-container
  // ------------------------------------
  // Contenedor del listado de subórdenes
  // ------------------------------------
  background-color: transparent

.quotation--without-suborders
  // ---------------------------------
  // Aviso de cotización sin productos
  // ---------------------------------
  padding-top: 50px
  padding-bottom: 50px
  margin-bottom: 0
  background-color: var(--v-auxbg-base)
  border-radius: 10px
  & > .quotation--text
    // ---------------
    // Texto del aviso
    // ---------------
    font-size: 24px
    font-weight: bold
    text-align: center

.quotation--suborders
  // ----------
  // Subórdenes
  // ----------
  border-radius: 10px
  margin-bottom: 15px

.quotation--suborders-head
  // -------------------------
  // Cabecera de cada suborden
  // -------------------------
  display: flex
  justify-content: space-between
  padding: 1rem 0
  align-items: center
  @media (800px > width)
    flex-direction: column
    align-items: flex-start
  .head-title
    // ------------------------------
    // Nombre y código de la suborden
    // ------------------------------
    color: var(--v-primary-base)
    font-family: "Roboto", sans-serif
    font-size: 1.25rem
    font-weight: 900
    letter-spacing: 0.0125em
    line-height: 2rem
    margin-bottom: 0
    @media (600px > width)
      font-size: 1rem
      letter-spacing: 0.009375em
      line-height: 1.75rem

.quotation--suborders-body
  // ----------------------------
  // Información de cada suborden
  // ----------------------------
  margin: 0
  margin-bottom: 1rem
  border-radius: 10px
  .body-title
    color: var(--v-primary-base)
    font-size: .9rem
    margin-bottom: 0
    font-weight: 500
  .body-text
    font-size: 1rem
    font-weight: 400

.theme--light
  .quotation--suborders
    background-color: rgb(0 0 0 / 3%)
    border: 1px solid rgb(0 0 0 / 0.26)
    border-left: 5px solid var(--v-primary-base)

.theme--dark
  .quotation--suborders
    background-color: rgb(40 40 40)
    border: 1px solid rgb(255 255 255 / 0.16)
    border-left: 5px solid var(--v-primary-base)
</style>
